import { useGraphQLQuery } from '@betterment/js-runtime';
import { Card, HStack, Heading, Icon, ListRow, Separator, Text } from '@betterment/samba-react';
import {
  CardLoader,
  ErrorBoundary,
  SambaAutoLink,
  SecurityGroupPositionsHoldingsContent,
} from '@betterment/shared-components';
import { SecurityPositionDetailsModeDropdown } from '@betterment/shared-components/src/components/holdings/SecurityPositionDetailsModeDropdown';
import { SecurityPositionRow } from '@betterment/shared-components/src/components/holdings/SecurityPositionRow';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { EnvelopeAccountHoldingsDocument } from './graphql/envelopeAccountHoldings.generated';

import { urlBuilder } from '../../../Shared/urlBuilder';

export const HoldingsCardView: React.FC = () => {
  const accountId = useRequiredParam('accountId');
  const { data } = useGraphQLQuery(EnvelopeAccountHoldingsDocument, {
    variables: { id: accountId },
    throwOnError: true,
  });

  if (!data) return <CardLoader />;
  const { account } = data;

  if (account.__typename === 'ManagedAccount') {
    return (
      <Card>
        <Card.Header css={{ padding: 'space.300' }}>
          <Heading as="h2" variant="title200">
            Holdings
          </Heading>
        </Card.Header>
        <Card.Content>
          <SecurityGroupPositionsHoldingsContent
            columns={['Asset', 'Balance']}
            positions={account.securityGroupPositions}
          />
        </Card.Content>
      </Card>
    );
  }

  if (account.__typename === 'DirectTradingAccount') {
    const { securityPositions: positions } = account;
    return (
      <Card>
        <Card.Header css={{ padding: 'space.300' }}>
          <HStack css={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Heading as="h2" variant="title200">
              Holdings
            </Heading>
            <SecurityPositionDetailsModeDropdown />
          </HStack>
        </Card.Header>
        <Card.Content>
          {positions.length > 0 ? (
            <>
              {positions.map(position => (
                <SambaAutoLink
                  key={position.financialSecurity.id}
                  css={{ textDecoration: 'none!important' }}
                  to={urlBuilder.appAccountSecurity.absolute({ accountId, securityId: position.financialSecurity.id })}
                >
                  <SecurityPositionRow
                    key={position.financialSecurity.id}
                    position={position}
                    css={{
                      color: 'text',
                      width: '100%',
                      transition: 'background-color 0.2s ease-in-out',
                      backgroundColor: 'background.transparent',
                      '&:hover': { backgroundColor: 'background.info', cursor: 'pointer' },
                    }}
                  >
                    <ListRow.TrailingAsset>
                      <Icon name="CaretRight" size="small" />
                    </ListRow.TrailingAsset>
                  </SecurityPositionRow>
                </SambaAutoLink>
              ))}
            </>
          ) : (
            <>
              <Separator css={{ fullWidthWithInset: 'space.300' }} />
              <Text as="p" color="text.weak" css={{ padding: 'space.300' }}>
                Purchased securities will show here.
              </Text>
            </>
          )}
          <Separator css={{ fullWidthWithInset: 'space.300' }} />
          <SambaAutoLink
            to={urlBuilder.appRolloverExpectations.absolute({ sub_account_id: account.legacySubAccountId })}
          >
            <Text as="p" css={{ padding: 'space.300' }}>
              Move securities to Betterment
            </Text>
          </SambaAutoLink>
        </Card.Content>
      </Card>
    );
  }

  return null;
};

export const HoldingsCard: React.FC = () => (
  <ErrorBoundary fallbackType="none">
    <HoldingsCardView />
  </ErrorBoundary>
);
